import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Sunrise, Sun, CloudSun, Moon, Sparkles, Quote, Wrench, Heart, AlertCircle, Flame,
  Battery, Info,
} from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import type { DayInLife, DayBlock } from "@/data/dayInLife";

interface Props {
  data: DayInLife;
}

const PERIOD_META: Record<DayBlock["period"], { icon: typeof Sunrise; label: string; tint: string; ring: string; iconColor: string; why: string }> = {
  morning:   { icon: Sunrise, label: "Morning",   tint: "from-amber-500/15 via-amber-400/5 to-transparent",  ring: "border-amber-500/30",  iconColor: "text-amber-400",
    why: "Mornings set the tone. Cognitive sharpness peaks early — the best practitioners protect this window for the highest-leverage work of the day." },
  midday:    { icon: Sun,     label: "Midday",    tint: "from-orange-500/15 via-orange-400/5 to-transparent", ring: "border-orange-500/30", iconColor: "text-orange-400",
    why: "Midday is collaboration territory. Energy is still high and most of the team is online — it's when alignment, decisions and shared progress happen." },
  afternoon: { icon: CloudSun,label: "Afternoon", tint: "from-sky-500/15 via-sky-400/5 to-transparent",      ring: "border-sky-500/30",    iconColor: "text-sky-400",
    why: "Afternoons are for shipping and review. Raw focus dips, but judgement is strong — ideal for refining, reviewing and pushing work over the line." },
  evening:   { icon: Moon,    label: "Evening",   tint: "from-indigo-500/15 via-indigo-400/5 to-transparent",ring: "border-indigo-500/30", iconColor: "text-indigo-300",
    why: "Evenings compound careers. Reflection, learning and lightweight craft here are what separate good practitioners from great ones over a decade." },
};

const ENERGY_META: Record<number, { label: string; tagline: string; tone: string }> = {
  1: { label: "Recharge",      tagline: "Low-stakes, restorative.",                           tone: "text-muted-foreground" },
  2: { label: "Light load",    tagline: "Steady, simple, easy to context-switch out of.",     tone: "text-sky-400" },
  3: { label: "Steady flow",   tagline: "Solid working pace — most of a week lives here.",    tone: "text-primary" },
  4: { label: "High output",   tagline: "You're locked in. Visible progress happens now.",    tone: "text-emerald-400" },
  5: { label: "Peak focus",    tagline: "The block your future self thanks you for.",         tone: "text-amber-400" },
};

function EnergyBar({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`Energy ${value}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-2.5 rounded-sm transition-colors ${i <= value ? "bg-primary" : "bg-muted"}`}
        />
      ))}
    </div>
  );
}

function EnergyDetail({ block, roleTagline }: { block: DayBlock; roleTagline: string }) {
  const meta = PERIOD_META[block.period];
  const energy = ENERGY_META[block.energy] ?? ENERGY_META[3];
  const Icon = meta.icon;
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            <Icon className={`h-3 w-3 ${meta.iconColor}`} /> {meta.label} · {block.time}
          </div>
          <p className="mt-1 font-display text-sm font-semibold text-foreground leading-snug">{block.title}</p>
        </div>
        <div className="shrink-0 text-right">
          <div className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${energy.tone}`}>
            <Battery className="h-3 w-3" /> {block.energy}/5
          </div>
          <div className={`mt-0.5 text-[11px] font-semibold ${energy.tone}`}>{energy.label}</div>
        </div>
      </div>

      <div className="rounded-md border border-border/50 bg-muted/30 p-2.5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">What this feels like</div>
        <p className="text-xs text-foreground leading-relaxed">{energy.tagline}</p>
      </div>

      <div className="rounded-md border border-primary/20 bg-primary/[0.04] p-2.5">
        <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-primary mb-1">
          <Info className="h-3 w-3" /> Why this time matters
        </div>
        <p className="text-xs text-foreground/90 leading-relaxed">{meta.why}</p>
        <p className="mt-1.5 text-[11px] italic text-muted-foreground leading-relaxed">In this role: {roleTagline}</p>
      </div>
    </div>
  );
}

function MobileEnergySheet({ block, roleTagline, trigger }: { block: DayBlock; roleTagline: string; trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const startY = useRef<number | null>(null);
  const deltaY = useRef(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLElement | null>(null);

  const findOverlay = () => {
    if (!overlayRef.current) {
      overlayRef.current = document.querySelector<HTMLElement>('[data-radix-dialog-overlay]')
        || document.querySelector<HTMLElement>('[data-state="open"].fixed.inset-0');
    }
    return overlayRef.current;
  };

  const setDrag = (y: number) => {
    const el = contentRef.current;
    if (!el) return;
    el.style.transition = "none";
    el.style.transform = y > 0 ? `translate3d(0, ${y}px, 0)` : "";
    const ov = findOverlay();
    if (ov) {
      const h = el.getBoundingClientRect().height || window.innerHeight;
      const progress = Math.min(1, Math.max(0, y / h));
      ov.style.transition = "none";
      ov.style.opacity = String(1 - progress * 0.85);
    }
  };

  const animateBack = () => {
    const el = contentRef.current;
    if (el) {
      el.style.transition = "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)";
      el.style.transform = "";
    }
    const ov = findOverlay();
    if (ov) {
      ov.style.transition = "opacity 320ms cubic-bezier(0.22, 1, 0.36, 1)";
      ov.style.opacity = "";
    }
  };

  const animateOut = () => {
    const el = contentRef.current;
    if (el) {
      const h = el.getBoundingClientRect().height || window.innerHeight;
      el.style.transition = "transform 260ms cubic-bezier(0.32, 0.72, 0, 1)";
      el.style.transform = `translate3d(0, ${h}px, 0)`;
    }
    const ov = findOverlay();
    if (ov) {
      ov.style.transition = "opacity 260ms cubic-bezier(0.32, 0.72, 0, 1)";
      ov.style.opacity = "0";
    }
    setTimeout(() => {
      setOpen(false);
      requestAnimationFrame(() => {
        if (contentRef.current) {
          contentRef.current.style.transition = "";
          contentRef.current.style.transform = "";
        }
        if (overlayRef.current) {
          overlayRef.current.style.transition = "";
          overlayRef.current.style.opacity = "";
        }
        overlayRef.current = null;
      });
    }, 240);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    deltaY.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    const el = contentRef.current;
    const atTop = !el || el.scrollTop <= 0;
    if (dy > 0 && atTop) {
      deltaY.current = dy;
      setDrag(dy);
    }
  };
  const onTouchEnd = () => {
    const dy = deltaY.current;
    startY.current = null;
    deltaY.current = 0;
    if (dy > 80) {
      animateOut();
    } else if (dy > 0) {
      animateBack();
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (v) setOpen(true); else animateOut(); }}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        ref={contentRef}
        side="bottom"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onPointerDownOutside={(e) => { e.preventDefault(); animateOut(); }}
        onEscapeKeyDown={(e) => { e.preventDefault(); animateOut(); }}
        className="rounded-t-2xl border-t border-border/60 px-5 pb-8 pt-5 max-h-[85vh] overflow-y-auto touch-pan-y will-change-transform data-[state=open]:duration-[320ms] data-[state=closed]:duration-[240ms] data-[state=open]:ease-[cubic-bezier(0.22,1,0.36,1)] data-[state=closed]:ease-[cubic-bezier(0.32,0.72,0,1)]"
      >
        <div
          className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-muted-foreground/30"
          aria-hidden
        />
        <SheetHeader className="sr-only">
          <SheetTitle>Energy detail for {block.title}</SheetTitle>
        </SheetHeader>
        <EnergyDetail block={block} roleTagline={roleTagline} />
      </SheetContent>
    </Sheet>
  );
}

function EnergyTrigger({ block, roleTagline }: { block: DayBlock; roleTagline: string }) {
  const isMobile = useIsMobile();

  const triggerBtn = (
    <button
      type="button"
      onClick={(e) => e.stopPropagation()}
      className="group inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 -mr-1.5 hover:bg-background/60 active:bg-background/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      aria-label={`Energy ${block.energy} of 5. Tap for why this time of day matters.`}
    >
      <EnergyBar value={block.energy} />
      <Info className="h-3 w-3 text-muted-foreground/60 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100 transition-opacity" />
    </button>
  );

  if (isMobile) {
    return <MobileEnergySheet block={block} roleTagline={roleTagline} trigger={triggerBtn} />;
  }

  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>{triggerBtn}</HoverCardTrigger>
      <HoverCardContent align="end" sideOffset={8} className="w-72">
        <EnergyDetail block={block} roleTagline={roleTagline} />
      </HoverCardContent>
    </HoverCard>
  );
}

function WeekShape({ shape }: { shape: DayInLife["weekShape"] }) {
  const items = [
    { key: "Deep work",   value: shape.deepWork,  color: "bg-primary" },
    { key: "Meetings",    value: shape.meetings,  color: "bg-accent" },
    { key: "Learning",    value: shape.learning,  color: "bg-emerald-500" },
    { key: "Admin",       value: shape.admin,     color: "bg-muted-foreground/50" },
  ];
  return (
    <div className="space-y-2">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
        {items.map((it) => (
          <div key={it.key} className={it.color} style={{ width: `${it.value}%` }} title={`${it.key}: ${it.value}%`} />
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
        {items.map((it) => (
          <div key={it.key} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className={`h-2 w-2 rounded-full ${it.color}`} />
            <span className="font-medium text-foreground">{it.value}%</span>
            <span className="truncate">{it.key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DayInLifeTimeline({ data }: Props) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.06] via-card to-accent/[0.04]">
      {/* Header */}
      <div className="relative px-5 sm:px-6 pt-6 pb-5 border-b border-border/40">
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 border border-primary/30 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
          <Sparkles className="h-3 w-3" /> A day in the life
        </div>
        <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold text-foreground leading-tight">
          {data.tagline}
        </h3>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {data.vibe.map((v) => (
            <span key={v} className="rounded-full border border-border/60 bg-card/60 px-2.5 py-0.5 text-[11px] text-muted-foreground">
              {v}
            </span>
          ))}
        </div>
      </div>

      <div className="px-5 sm:px-6 py-5 space-y-6">
        {/* Week shape */}
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Where a typical week goes
          </h4>
          <WeekShape shape={data.weekShape} />
        </div>

        {/* Timeline */}
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            One day, hour by hour
          </h4>
          <ol className="relative space-y-3 pl-6 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-gradient-to-b before:from-primary/40 before:via-border before:to-transparent">
            {data.blocks.map((b, i) => {
              const meta = PERIOD_META[b.period];
              const Icon = meta.icon;
              return (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="relative"
                >
                  <span className={`absolute -left-[22px] top-1 flex h-6 w-6 items-center justify-center rounded-full border bg-background ${meta.ring}`}>
                    <Icon className={`h-3.5 w-3.5 ${meta.iconColor}`} />
                  </span>
                  <div className={`rounded-xl border border-border/50 bg-gradient-to-br ${meta.tint} p-3.5 hover:border-primary/30 transition-colors`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] font-semibold text-primary tabular-nums">{b.time}</span>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{meta.label}</span>
                      </div>
                      <EnergyTrigger block={b} roleTagline={data.tagline} />

                    </div>
                    <p className="mt-1.5 font-display text-sm font-semibold text-foreground">{b.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{b.detail}</p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>

        {/* Best / Hardest */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.05] p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-400">
              <Flame className="h-3 w-3" /> Best part
            </div>
            <p className="mt-1.5 text-sm text-foreground leading-relaxed">{data.bestPart}</p>
          </div>
          <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-400">
              <AlertCircle className="h-3 w-3" /> Hardest part
            </div>
            <p className="mt-1.5 text-sm text-foreground leading-relaxed">{data.hardestPart}</p>
          </div>
        </div>

        {/* Voice */}
        {data.voice && (
          <figure className="relative rounded-xl border border-border/50 bg-card/60 p-4 pl-10">
            <Quote className="absolute left-3 top-3 h-5 w-5 text-primary/60" />
            <blockquote className="text-sm italic text-foreground leading-relaxed">
              "{data.voice.quote}"
            </blockquote>
            <figcaption className="mt-2 text-[11px] text-muted-foreground">
              <span className="font-semibold text-foreground">{data.voice.name}</span> — {data.voice.role}
            </figcaption>
          </figure>
        )}

        {/* Tools */}
        {data.tools.length > 0 && (
          <div>
            <h4 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              <Wrench className="h-3 w-3" /> Tools they live in
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {data.tools.map((t) => (
                <span key={t} className="rounded-md border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] text-foreground">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Heart className="h-3 w-3 text-primary/70" />
          {data.derived
            ? "Built from this role's own signals, not from an interview with someone else. Your day will vary by company, city and seniority."
            : "Curated from real practitioners. Your day will vary by company, city, and seniority."}
        </p>

      </div>
    </div>
  );
}
